import { useEffect, type ReactNode } from 'react';
import { cn } from '~/lib/cn';

interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  confirming?: boolean;
  tone?: 'default' | 'destructive';
}

/**
 * Centered modal confirm dialog. Backdrop + Escape cancel; the primary
 * action is auto-focused so Enter commits. Destructive tone paints the
 * confirm button red and the icon amber. Narrower than SlideOver (short
 * question + two buttons); reused across destructive actions.
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  body,
  confirmLabel,
  confirming,
  tone = 'default',
}: ConfirmDialogProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const destructive = tone === 'destructive';

  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-6" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label="Cancel"
        onClick={onCancel}
        className="absolute inset-0 cursor-default bg-black/35 backdrop-blur-[1px] motion-safe:animate-[fadeIn_120ms_ease-out]"
      />
      <div
        className={cn(
          'relative w-full max-w-[400px] rounded-lg bg-surface-plate p-5 shadow-[0_0_0_1px_rgba(10,10,10,0.08),0_24px_56px_-24px_rgba(10,10,10,0.4)]',
          'motion-safe:animate-[popIn_140ms_cubic-bezier(0.32,0.72,0,1)]',
        )}
      >
        <h2 className="text-[15px] font-semibold -tracking-[0.01em] text-ink-primary">
          {title}
        </h2>
        {body && (
          <div className="mt-2 text-[14px] leading-[18px] text-ink-secondary">
            {body}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            disabled={confirming}
            className={cn(
              'inline-flex h-9 items-center justify-center rounded-md px-4 text-[14px] font-semibold text-ink-on-dark disabled:opacity-60',
              destructive ? 'bg-stop hover:bg-stop/90' : 'bg-ink-primary hover:bg-ink-primary/90',
            )}
          >
            {confirming ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
